import {
	ChangeEvent,
	FunctionComponent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import useLocalStorageState from "use-local-storage-state";

import { faColumns3, faRadar, faSliders } from "@fortawesome/pro-regular-svg-icons";

import { Table, TableColumn } from "@/components/Table/Table";
import { TItem } from "@/components/SortableList/SortableList";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Icon, Modal } from "@/components";
import { useAddToListConfirm } from "@/components/AddToListConfirm/useAddToListConfirm";
import { InputField } from "@/components/InputField/InputField";
import { Radio } from "@/components/Radio/Radio";
import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";
import { Order, SortParams } from "@/types/types";
import { useCopyToClipboard } from "@/utils/useCopyToClipboard";
import { useSnackBar } from "@/utils/useSnackbar";
import { useSearch } from "@/utils";
import { formatCappedCount } from "@/utils/formatCappedCount";
import { useAppSelector } from "@/redux/hooks";

import {
	useLazyGetOrganizationsQuery,
	useOrganizationAddToListMutation,
	useOrganizationAddToTrackerMutation,
	useRequestOrganizationsExportMutation,
} from "../../../redux/organization/organization.api";
import { useTrackerDestinationLists } from "../../List/utils/useTrackerDestinationLists";
import { organizationSelector } from "../../../redux/organization/organization.slice";
import { ContactSelectType } from "../schema/contacts";
import { ContactsCompanyFilters } from "../constants/contacts-filter";
import { useContactsUrlState } from "../hooks/useContactsUrlState";
import { useOrganizationSelection } from "../hooks/useOrganizationSelection";
import { CompaniesTableColumns } from "./TableColumnConfigs/CompaniesTableColumns";
import { ContactsColumnModal } from "./ContactsColumnModal";
import { ContactsFilterModal } from "./ContactsFilterModal";
import { buildOrganizationQueryParams } from "../utils/buildOrganizationQueryParams";

export const CompaniesTable: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`contacts.${key}`), [t]);

	const {
		page,
		setPage,
		limit,
		setLimit,
		sortingInbox,
		setSortingInbox,
		filter,
		setFilter,
		applyFilters,
		searchValue,
		setSearchValue,
		query,
		semanticSearchOrganizations,
		setSemanticSearchOrganizations,
	} = useContactsUrlState({
		prefix: "companies",
		defaultSortBy: "organization.updatedAt",
		defaultSortOrder: Order.DESC,
		withSearch: true,
	}) as ReturnType<typeof useContactsUrlState> & {
		searchValue: string;
		setSearchValue: (v: string) => void;
		query: string;
		isTyping: boolean;
	};

	const [showEdit, setShowEdit] = useState<{ id: number; name: string } | undefined>(undefined);
	const [showFilterModal, setShowFilterModal] = useState(false);
	const [showSelectColumns, setShowSelectColumns] = useState(false);
	const [tableConfig, setTableConfig] = useLocalStorageState<TItem[]>(
		"contacts-columns-companies",
		{
			defaultValue: [],
		}
	);

	const { renderSnackbar, handleCopyClick } = useCopyToClipboard();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();

	const { organizations: data, organizationsMeta: meta } = useAppSelector(organizationSelector);

	const {
		selectedItems,
		selectAll,
		showAll,
		setShowAll,
		prepSelectType,
		setPrepSelectType,
		hasSelection,
		selectedCount,
		getSelectionPayload,
		toggleSelectAll,
		handleSetSelectType,
		handleSelectItem,
		clearSelection,
	} = useOrganizationSelection({ data, itemCount: meta?.itemCount, filter, query });

	const [triggerGetOrganizations, { isFetching }] = useLazyGetOrganizationsQuery();
	const lastQueryPromise = useRef<ReturnType<typeof triggerGetOrganizations> | null>(null);

	const queryParams = useMemo(
		() =>
			buildOrganizationQueryParams({
				page,
				limit,
				sortingInbox,
				query,
				filter,
				semanticSearchOrganizations,
			}),
		[page, limit, sortingInbox, query, filter, semanticSearchOrganizations]
	);

	useEffect(() => {
		lastQueryPromise.current?.abort();
		lastQueryPromise.current = triggerGetOrganizations(queryParams);
	}, [queryParams, triggerGetOrganizations]);

	const [addToList, { isLoading: isAddToListLoading }] = useOrganizationAddToListMutation();
	const [addToTracker, { isLoading: isAddToTrackerLoading }] =
		useOrganizationAddToTrackerMutation();
	const [requestExport, { isLoading: isExportLoading }] = useRequestOrganizationsExportMutation();

	// Tracker state
	const [showAddToTrackerModal, setShowAddToTrackerModal] = useState(false);
	const [selectedTracker, setSelectedTracker] = useState<AutoCompleteItem | undefined>(undefined);
	const [singleTrackerOrgId, setSingleTrackerOrgId] = useState<number | null>(null);
	const { setSearchValue: setTrackerSearchValue, query: trackerQuery } = useSearch();

	const {
		lists: trackerLists,
		isLoading: isLoadingTrackerLists,
		isFetching: isFetchingTrackerLists,
	} = useTrackerDestinationLists({
		searchValue: trackerQuery,
		includeJobChange: false,
		active: true,
	});

	// ── Add to list ──────────────────────────────────────────────────────
	const addToListConfirm = useAddToListConfirm({
		selection: { count: selectedCount, source: "companies" },
		send: (target) => addToList({ ...getSelectionPayload(), ...target }).unwrap(),
		showMessage: handleOpenSnackbar,
	});

	// ── Export ────────────────────────────────────────────────────────────
	// The file is built off-request and announced by a notification (PREC-3953), so this only
	// hands over the selection.
	const handleExport = useCallback(async () => {
		try {
			await requestExport(getSelectionPayload()).unwrap();
			handleOpenSnackbar(t("contacts.exportStarted"), 6000);
		} catch (error) {
			console.error(error);
			handleOpenSnackbar(t("basics.error"));
		}
	}, [requestExport, getSelectionPayload, handleOpenSnackbar, t]);

	// ── Add to tracker ──────────────────────────────────────────────────
	const handleOpenTrackerModal = useCallback((organizationId?: number) => {
		if (organizationId) {
			setSingleTrackerOrgId(organizationId);
		}

		setShowAddToTrackerModal(true);
	}, []);

	const handleConfirmAddToTracker = useCallback(async () => {
		if (!selectedTracker) {
			return;
		}

		try {
			let payload;

			if (singleTrackerOrgId) {
				payload = {
					selectType: ContactSelectType.CURRENT_PAGE,
					ids: [singleTrackerOrgId],
					listId: Number(selectedTracker.id),
				};
			} else {
				const selectionPayload = getSelectionPayload();

				payload = {
					...selectionPayload,
					listId: Number(selectedTracker.id),
				};
			}

			await addToTracker(payload).unwrap();
			handleOpenSnackbar(t("contacts.addedToTracker"));
		} catch {
			handleOpenSnackbar(t("basics.error"));
		} finally {
			setShowAddToTrackerModal(false);
			setSingleTrackerOrgId(null);
			setSelectedTracker(undefined);
		}
	}, [
		selectedTracker,
		singleTrackerOrgId,
		addToTracker,
		getSelectionPayload,
		handleOpenSnackbar,
		t,
	]);

	// ── Table helpers ────────────────────────────────────────────────────
	const handleChangeRowsPerPage = (newLimit: number) => {
		setLimit(newLimit);
	};

	const handleSorting = (sortParams: SortParams) => {
		if (!sortParams.sortBy) {
			setSortingInbox({ sortBy: "organization.updatedAt", sortOrder: Order.DESC });

			return;
		}

		setSortingInbox(sortParams);
	};

	const columns = useMemo(() => {
		let cols = CompaniesTableColumns({
			handleCopyClick,
			ts,
			sortingInbox,
			setShowEdit,
			showEdit,
			setShowAll,
			toggleSelectAll,
			selectAll,
			selectedItems,
			handleSelectItem,
			onAddToTracker: handleOpenTrackerModal,
		});

		if (tableConfig?.length) {
			for (const c of cols) {
				const configIndex = tableConfig.findIndex((tc) => tc?.uid === c.uid);
				const currentIndex = cols.findIndex((tc) => tc?.uid === c.uid);

				if (configIndex > -1 && currentIndex > -1 && configIndex !== currentIndex) {
					const newConfigColumn = cols[currentIndex];

					cols.splice(currentIndex, 1);
					cols = [...cols.slice(0, configIndex), newConfigColumn, ...cols.slice(configIndex)];
				}
			}

			cols = cols.map((c) => {
				const config = tableConfig.find((tc) => tc?.uid === c?.uid);

				if (config) {
					c.show = config.show;
					c.locked = config.locked;
				}

				return c;
			});
		}

		return cols;
	}, [
		handleCopyClick,
		ts,
		sortingInbox,
		showEdit,
		tableConfig,
		selectAll,
		selectedItems,
		toggleSelectAll,
		handleSelectItem,
	]);

	const resetTableConfig = (force = false) => {
		const sourceCols = force
			? CompaniesTableColumns({
					handleCopyClick,
					ts,
					sortingInbox,
					setShowEdit,
					showEdit,
					setShowAll,
					toggleSelectAll,
					selectAll,
					selectedItems,
					handleSelectItem,
					onAddToTracker: handleOpenTrackerModal,
				})
			: columns;

		const newConfig: TItem[] = sourceCols.map((c: TableColumn, index: number) => ({
			uid: c.uid ?? "",
			id: index + 1,
			show: typeof c.show !== "undefined" ? c.show : true,
			content: c.title,
			locked: !!c.locked || false,
			allowExclude: typeof c.allowExclude !== "undefined" ? c.allowExclude : true,
			custom: false,
			moveable: c.moveable,
			hideable: c.hideable,
		}));

		if (tableConfig.length === 0 || force) {
			setTableConfig(newConfig);
		}
	};

	useMemo(() => {
		if (columns.length > 0 && tableConfig.length === 0) {
			resetTableConfig();
		}
	}, [columns]);

	return (
		<div className="w-full">
			<div className="flex items-center gap-2 h-11 px-2 bg-white border-b border-border-light">
				<div className="w-[280px] flex-shrink-0">
					<InputField
						className="!rounded-[10px]"
						classNameWrapper="!mb-0"
						handleChange={(e: ChangeEvent<HTMLInputElement>) => {
							setSearchValue(e.target.value);
						}}
						name="search"
						placeholder={ts("searchPlaceholder")}
						size={ButtonSize.XS}
						value={searchValue}
					/>
				</div>
				<div className="ml-auto flex flex-row items-center gap-2">
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faColumns3} />}
							size={ButtonSize.S}
							title={ts("selectColumns")}
							onClick={() => setShowSelectColumns(true)}
						/>
					</div>
					<div className="w-fit">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							image={<Icon className="mr-2" icon={faSliders} />}
							size={ButtonSize.S}
							title={`${ts("filter")}${filter && Object.keys(filter).length > 0 ? ` (${Object.keys(filter).length})` : ""}`}
							onClick={() => setShowFilterModal(true)}
						/>
					</div>
				</div>
			</div>

			<Table
				addToAnotherList={
					hasSelection
						? {
								isLoading: isAddToListLoading,
								onCreateNew: addToListConfirm.onCreateNew,
								onSelectList: addToListConfirm.onSelectList,
							}
						: undefined
				}
				addToProfileTracker={
					hasSelection
						? {
								addToProfileTracker: () => handleOpenTrackerModal(),
								addToProfileTrackerIsLoading: isAddToTrackerLoading,
							}
						: undefined
				}
				clearSelection={clearSelection}
				columns={columns}
				data={data || []}
				exportIsLoading={isExportLoading}
				filter={filter}
				handleChangeRowsPerPage={handleChangeRowsPerPage}
				handleExport={hasSelection ? handleExport : undefined}
				handleFetchPage={setPage}
				handleSorting={handleSorting}
				isLoading={isFetching}
				limit={limit}
				overallCount={meta?.itemCount}
				page={page}
				selectedCount={selectedCount}
				showFooterFunctions={hasSelection}
				sticky
				tableConfig={tableConfig}
				totalPages={meta?.pageCount}
			/>

			{showSelectColumns && (
				<ContactsColumnModal
					handleResetToDefault={() => {
						setTableConfig([]);
						setTimeout(() => resetTableConfig(true), 100);
					}}
					setShowSelectColumns={setShowSelectColumns}
					setTableConfig={setTableConfig}
					showSelectColumns={showSelectColumns}
					tableConfig={tableConfig}
				/>
			)}

			{showFilterModal && (
				<ContactsFilterModal
					applyFilters={applyFilters}
					filter={filter}
					filterDataType={ContactsCompanyFilters}
					semanticSearchOrganizations={semanticSearchOrganizations}
					setFilter={setFilter}
					setSemanticSearchOrganizations={setSemanticSearchOrganizations}
					setShowFilterModal={setShowFilterModal}
					showFilterModal={showFilterModal}
				/>
			)}

			{showAll && (
				<Modal
					handleClose={() => setShowAll(false)}
					handleSave={async () => {
						handleSetSelectType(prepSelectType);
					}}
					isOpened={showAll}
					size="xs"
					submitButtonText={t("basics.confirm")}
					title={ts("select")}
				>
					<div className="flex flex-col items-start gap-4">
						<Radio
							isChecked={prepSelectType === ContactSelectType.ALL}
							onChange={() => {
								setPrepSelectType(ContactSelectType.ALL);
							}}
						>
							<>
								{ts("all")}{" "}
								<span className="text-xs bg-placeholder text-white ml-2 px-2 py-1 rounded-md">
									{formatCappedCount(meta?.itemCount, meta?.isCapped)}
								</span>
							</>
						</Radio>
						<Radio
							isChecked={prepSelectType === ContactSelectType.CURRENT_PAGE}
							onChange={() => {
								setPrepSelectType(ContactSelectType.CURRENT_PAGE);
							}}
						>
							<>
								{ts("currentPage")}{" "}
								<span className="text-xs bg-placeholder text-white ml-2 px-2 py-1 rounded-md">
									{meta?.itemCount && meta?.itemCount < limit ? meta?.itemCount : limit}
								</span>
							</>
						</Radio>
					</div>
				</Modal>
			)}

			{addToListConfirm.modal}

			{showAddToTrackerModal && (
				<Modal
					handleClose={() => {
						setShowAddToTrackerModal(false);
						setSingleTrackerOrgId(null);
						setSelectedTracker(undefined);
					}}
					handleSave={handleConfirmAddToTracker}
					isLoading={isAddToTrackerLoading}
					isOpened={showAddToTrackerModal}
					size="md"
					submitButtonText={t("basics.confirm")}
					title={ts("addToTrackerTitle")}
				>
					<div className="mb-4 flex flex-row items-center">
						{!isLoadingTrackerLists ? (
							<AutoComplete
								data={
									trackerLists.map((list) => {
										return { id: list.id, title: list.name } as AutoCompleteItem;
									}) || []
								}
								handleSearch={(value: string) => setTrackerSearchValue(value)}
								handleSelect={(value?: AutoCompleteItem) => setSelectedTracker(value)}
								icon={faRadar}
								isLoading={isLoadingTrackerLists || isFetchingTrackerLists}
								label={ts("trackerList")}
								placeholder={ts("searchTracker")}
								selectedItem={selectedTracker?.title || ""}
							/>
						) : (
							""
						)}
					</div>
				</Modal>
			)}

			{renderSnackbar()}
			{renderMessageSnackbar()}
		</div>
	);
};
